import { useMemo, useRef, VoidFunctionComponent } from 'react';
import { OnlyBotThree } from '@animavirtuality/onlybots-three';
import { useFrame } from '@react-three/fiber';
import { BackSide, Group } from 'three';
import { useTexture, GradientTexture, useMask } from '@react-three/drei';
import { OnlyBot } from '@animavirtuality/onlybots-core';
import schematicPartImg from '@/img/schematic-comp-part-1.png';
import { ThreeBotRenderer } from '@/components/BotRenderer';
import { oscillate } from '@/lib/mathUtils';
import { presets } from '@/lib/presets';
import { useMemoDisposable } from '@/hooks/useMemoDisposable';

const schematicRatio = 812 / 591;
const BASE_BOT_SCALE = 0.5;
const BASE_BOT_SIZE = 8;

interface Props {
    bot: OnlyBot;
    legendary: boolean;
    gradientColors: [string, string];
}
export const PortalContent: VoidFunctionComponent<Props> = ({ bot, gradientColors }) => {
    const schematicPart = useTexture(schematicPartImg);
    const stencil = useMask(1);
    const groupRef = useRef<Group>(new Group());
    const timer = useRef<number>(0);

    useFrame((_, delta) => {
        if (bot.anchor.y >= 1) {
            timer.current += delta;
            groupRef.current.position.y = oscillate(-0.15, 0.15, timer.current, 2);
        }
    });

    const threeBot = useMemoDisposable(() => OnlyBotThree.create(bot, presets), [bot]);

    const botScale = useMemo(() => {
        let scale = 0.5;
        if (threeBot) {
            const width = threeBot.max.x - threeBot.min.x + 1;
            const widthRatio = (BASE_BOT_SIZE / width) * BASE_BOT_SCALE * 1.1; // magic number!

            const height = threeBot.max.y - threeBot.min.y + 1;
            const heightRatio = (BASE_BOT_SIZE / height) * BASE_BOT_SCALE;

            if (width > 10 && height > 10) {
                scale = 0.25;
            } else {
                scale = widthRatio < heightRatio ? widthRatio : heightRatio;
            }
        }
        return scale > 0.5 ? 0.5 : scale;
    }, [threeBot]);

    return (
        <>
            <group ref={groupRef} scale={botScale}>
                <ThreeBotRenderer bot={threeBot} zPos={-1.5} useStencil />
            </group>
            <group position={[0, 0, -5]} scale={10}>
                <mesh position={[0, 0.5, 0]}>
                    <planeGeometry attach="geometry" args={[1, 1 / schematicRatio]} />
                    <meshStandardMaterial {...stencil} color="white" map={schematicPart} transparent />
                </mesh>
                <mesh position={[0, -0.5, 0]}>
                    <planeGeometry attach="geometry" args={[1, 1 / schematicRatio]} />
                    <meshStandardMaterial color="white" map={schematicPart} transparent {...stencil} />
                </mesh>
            </group>

            <mesh position={[0, 0, 0]}>
                <sphereGeometry args={[10, 10]} />
                <meshBasicMaterial side={BackSide} {...stencil}>
                    <GradientTexture
                        stops={[0, 1]} // As many stops as you want
                        colors={gradientColors} // Colors need to match the number of stops
                        size={100} // Size is optional, default = 1024
                    />
                </meshBasicMaterial>
            </mesh>
        </>
    );
};
